import { useAuth } from "@/features/auth";
import {
    Alert,
    AlertDescription,
    Button,
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
    LoadingIndicator,
} from "@/shared/components";
import { ErrorBoundary, ErrorDisplay } from "@/shared/components/error";
import { InteractionStatus } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import { useState } from "react";
import { Outlet } from "react-router";

/**
 * Login page component
 * Handles authentication flow and redirects to home page when authenticated
 */
export default function LoginPage() {
    const { login, isAuthenticated, isLoading, error } = useAuth();
    const { inProgress } = useMsal();
    const [loginError, setLoginError] = useState<Error | null>(null);

    const isProcessingAuth = inProgress !== InteractionStatus.None;

    const handleLogin = () => {
        setLoginError(null);
        try {
            login();
        } catch (error) {
            setLoginError(error instanceof Error ? error : new Error("Inloggen mislukt"));
        }
    };

    if (isAuthenticated) {
        return <Outlet />;
    }

    return (
        <ErrorBoundary
            fallback={(error, reset) => (
                <ErrorDisplay error={error} variant="section" title="Fout bij het laden van de inlogpagina" onRetry={reset} />
            )}
        >
            <div className="bg-background flex h-full w-full flex-col items-center justify-center p-4">
                <Card className="w-full max-w-md shadow-lg">
                    <CardHeader className="space-y-2 text-center">
                        <CardTitle className="text-3xl font-bold">Welkom bij SEIN</CardTitle>
                        <CardDescription className="text-base">
                            {isLoading || isProcessingAuth
                                ? "Authenticeren met Microsoft..."
                                : "Log in met je NS-account om verder te gaan"}
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6 pt-6">
                        {(error || loginError) && (
                            <Alert variant="destructive">
                                <AlertDescription>
                                    {error?.message ?? loginError?.message ?? "Er is een fout opgetreden bij het inloggen"}
                                </AlertDescription>
                            </Alert>
                        )}

                        {isLoading || isProcessingAuth ? (
                            <div className="flex flex-col items-center justify-center py-8">
                                <LoadingIndicator size="medium" message="Doorverwijzen naar Microsoft login..." />
                            </div>
                        ) : (
                            <Button onClick={handleLogin} disabled={isLoading || isProcessingAuth} className="w-full py-6">
                                Inloggen met Microsoft
                            </Button>
                        )}
                    </CardContent>
                </Card>
            </div>
        </ErrorBoundary>
    );
}
